import {
  AssignLicenseRequest,
  EditLicenseRequest,
  License,
  LicenseAuditHistory,
  LicenseDeleteRequestUrlParams,
  LicenseListRequest,
  LicenseListResponse,
  LicensePostRequest,
  LicenseRenewalRequest,
  LicenseSelfServeRenewableListRequest,
  LicenseSelfServeRenewableListResponse,
  LicenseSelfServeRenewalRequest,
  LicenseSelfServeRenewalResponse,
  UnassignLicenseRequest,
  LicenseAlignmentResponse,
  LicenseAlignmentRequest,
} from '../stores/types/licenses.interface';
import { API_KEYS, Api } from './base.api';
import { SCOPE_TYPE, providesListTag } from './utils';

export const licenseApi = Api.injectEndpoints({
  endpoints: (builder) => ({
    fetchLicenseByLicenseKey: builder.query<
      License,
      { scope?: SCOPE_TYPE; companyId?: string; licenseKey?: string }
    >({
      query: (payload) => ({
        url: `/internal/core/v1/${API_KEYS.LICENSE}/${payload.licenseKey}`,
        params: { scope: payload.scope, companyId: payload.companyId },
        method: 'GET',
      }),
      providesTags: (result, error, arg) => [{ type: API_KEYS.LICENSE, id: result?.licenseKey }],
    }),
    fetchLicenseList: builder.query<LicenseListResponse, LicenseListRequest>({
      query: (param) => ({
        url: `/internal/core/v1/${API_KEYS.LICENSE}`,
        params: param,
        method: 'GET',
      }),
      providesTags: (result, error, arg) =>
        providesListTag(
          result?.entities.map((license) => ({ id: license.licenseKey })),
          API_KEYS.LICENSE,
        ),
    }),
    fetchLicenseSelfServeRenewableList: builder.query<
      LicenseSelfServeRenewableListResponse,
      LicenseSelfServeRenewableListRequest
    >({
      query: (param) => ({
        url: `/internal/core/v1/${API_KEYS.LICENSE}/${API_KEYS.LICENSE_SELF_SERVE}/${API_KEYS.RENEWABLE}`,
        params: param,
        method: 'GET',
      }),
      providesTags: (result, error, arg) =>
        providesListTag(
          result?.entities.map((renewable) => ({ id: renewable.licenseId })),
          API_KEYS.LICENSE_SELF_SERVE,
        ),
    }),
    assignLicense: builder.mutation<License, AssignLicenseRequest>({
      query: (request) => ({
        url: `/internal/core/v1/${API_KEYS.LICENSE}/assign/${request.licenseAssignRequestUrlParam.licenseKey}`,
        params: {
          scope: request.licenseAssignRequestUrlParam.scope,
          companyId: request.licenseAssignRequestUrlParam.companyId,
        },
        body: request.licenseAssignRequestBody,
        method: 'PATCH',
        toast: {
          successTrans: request.licenseAssignRequestToast.isChargerDeployed
            ? 'assign_charger_success_msg'
            : 'assign_undeployed_charger_success_msg',
          failedTrans: 'assign_charger_failure_msg',
          values: {
            chargerName: request.licenseAssignRequestToast.chargerName,
          },
        },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: API_KEYS.LICENSE, id: 'LIST' },
        {
          type: API_KEYS.LICENSE,
          id: arg.licenseAssignRequestUrlParam.licenseKey,
        },
        { type: API_KEYS.CHARGERS, id: 'LIST' },
        { type: API_KEYS.LICENSE_AUDIT_HISTORY, id: result?.licenseId },
      ],
    }),

    addLicense: builder.mutation<License, { license: LicensePostRequest; userScope: string }>({
      query: ({ license, userScope }) => ({
        url: `/internal/core/v1/${API_KEYS.LICENSE}`,
        params: { scope: userScope, companyId: license.companyId },
        method: 'POST',
        body: license,
        toast: {
          successTrans: 'license_add_license_success',
          failedTrans: 'license_add_license_fail',
          values: {
            numOfLicenses: license.numberOfLicenses,
          },
        },
      }),
      invalidatesTags: (result, error, arg) => [{ type: API_KEYS.LICENSE, id: 'LIST' }],
    }),
    fetchLicenseAuditHistoryByLicenseId: builder.query<
      LicenseAuditHistory[],
      { scope?: SCOPE_TYPE; companyId?: string; licenseId?: string }
    >({
      query: (payload) => ({
        url: `/internal/core/v1/${API_KEYS.LICENSE}/${API_KEYS.LICENSE_AUDIT_HISTORY}/${payload.licenseId}`,
        params: { scope: payload.scope, companyId: payload.companyId },
        method: 'GET',
      }),
      providesTags: (result, error, arg) =>
        result
          ? result.map((auditHistory) => ({
              type: API_KEYS.LICENSE_AUDIT_HISTORY,
              id: auditHistory.licenseId,
            }))
          : [{ type: API_KEYS.LICENSE_AUDIT_HISTORY, id: 'LIST' }],
    }),

    deleteLicense: builder.mutation<void, { urlParams: LicenseDeleteRequestUrlParams }>({
      query: ({ urlParams }) => ({
        url: `/internal/core/v1/${API_KEYS.LICENSE}/${urlParams.licenseKey}`,
        params: {
          scope: urlParams.scope,
          companyId: urlParams.companyId,
        },
        method: 'DELETE',
        toast: {
          successTrans: 'license_delete_success',
          failedTrans: 'license_delete_fail',
          values: {
            licenseKey: urlParams.licenseKey,
          },
        },
      }),
      invalidatesTags: (result, error, { urlParams }) => [
        { type: API_KEYS.LICENSE, id: urlParams.licenseKey },
        { type: API_KEYS.LICENSE, id: 'LIST' },
        { type: API_KEYS.CHARGERS, id: 'LIST' },
      ],
    }),

    editLicense: builder.mutation<License, EditLicenseRequest>({
      query: (request) => ({
        url: `/internal/core/v1/${API_KEYS.LICENSE}`,
        params: {
          scope: request.licenseEditRequestUrlParam.scope,
          companyId: request.licenseEditRequestUrlParam.companyId,
          bulkOperation: request.licenseEditRequestUrlParam.bulkOperation,
        },
        body: request.licenseEditRequestBody,
        method: 'PATCH',
        toast: {
          successTrans: 'edit_license_success',
          failedTrans: 'edit_license_failure',
        },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: API_KEYS.LICENSE, id: result?.licenseKey },
        { type: API_KEYS.LICENSE, id: 'LIST' },
        { type: API_KEYS.LICENSE_AUDIT_HISTORY, id: result?.licenseId },
      ],
    }),

    unassignLicense: builder.mutation<void, UnassignLicenseRequest>({
      query: (request) => ({
        url: `/internal/core/v1/${API_KEYS.LICENSE}/unassign/${request.licensePatchRequestUrlParam.licenseKey}`,
        params: {
          scope: request.licensePatchRequestUrlParam.scope,
          companyId: request.licensePatchRequestUrlParam.companyId,
        },
        method: 'PATCH',
        toast: {
          successTrans: 'license_unassign_success',
          failedTrans: 'license_unassign_fail',
          values: {
            licenseKey: request.licensePatchRequestToast.licenseKey,
            chargerName: request.licensePatchRequestToast.chargerName,
          },
        },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: API_KEYS.LICENSE, id: 'LIST' },
        {
          type: API_KEYS.LICENSE,
          id: arg.licensePatchRequestUrlParam.licenseKey,
        },
        { type: API_KEYS.CHARGERS, id: 'LIST' },
        {
          type: API_KEYS.LICENSE_AUDIT_HISTORY,
          id: arg.licensePatchRequestUrlParam.licenseKey,
        },
      ],
    }),

    renewLicense: builder.mutation<License, LicenseRenewalRequest>({
      query: (request) => ({
        url: `/internal/core/v1/${API_KEYS.LICENSE}/renew/${request.licenseRenewalRequestUrlParam.licenseKey}`,
        params: {
          scope: request.licenseRenewalRequestUrlParam.scope,
          companyId: request.licenseRenewalRequestUrlParam.companyId,
        },
        method: 'PATCH',
        body: request.licenseRenewalRequestBody,
        toast: {
          successTrans: 'license_renew_success',
          failedTrans: 'license_renew_fail',
        },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: API_KEYS.LICENSE, id: 'LIST' },
        { type: API_KEYS.CHARGERS, id: 'LIST' },
        {
          type: API_KEYS.LICENSE_AUDIT_HISTORY,
          id: result?.licenseId,
        },
      ],
    }),

    selfServeRenewLicense: builder.mutation<
      LicenseSelfServeRenewalResponse,
      LicenseSelfServeRenewalRequest
    >({
      query: (request) => ({
        url: `/internal/core/v1/${API_KEYS.LICENSE}/${API_KEYS.LICENSE_SELF_SERVE}/${API_KEYS.RENEWAL}`,
        params: {
          scope: request.params.scope,
          companyId: request.params.companyId,
        },
        method: 'POST',
        body: request.body,
        toast: {
          successTrans: 'license_self_renew_checkout_success',
          failedTrans: 'license_self_renew_checkout_fail',
        },
      }),
      invalidatesTags: (result, error, arg) => {
        const tags = [
          { type: API_KEYS.LICENSE, id: 'LIST' },
          { type: API_KEYS.CHARGERS, id: 'LIST' },
          { type: API_KEYS.LICENSE_SELF_SERVE, id: 'LIST' },
        ];
        result?.renewal?.licenses?.forEach((license) => {
          tags.push({
            type: API_KEYS.LICENSE_AUDIT_HISTORY,
            id: license.licenseId,
          });
        });
        return tags;
      },
    }),

    alignLicenseExpiryInfo: builder.mutation<LicenseAlignmentResponse, LicenseAlignmentRequest>({
      query: (request) => ({
        url: `/internal/core/v1/${API_KEYS.LICENSE_ALIGNMENT}/query`,
        params: {
          scope: request.params.scope,
          companyId: request.params.companyId,
        },
        method: 'POST',
        body: request.body,
      }),
    }),

    alignLicenseExpiry: builder.mutation<LicenseAlignmentResponse, LicenseAlignmentRequest>({
      query: (request) => ({
        url: `/internal/core/v1/${API_KEYS.LICENSE_ALIGNMENT}/set`,
        params: {
          scope: request.params.scope,
          companyId: request.params.companyId,
        },
        method: 'POST',
        body: request.body,
        toast: {
          successTrans: 'license_alignment_success',
          failedTrans: 'license_alignment_fail',
        },
      }),
      invalidatesTags: (result, error, arg) => [{ type: API_KEYS.LICENSE, id: 'LIST' }],
    }),
  }),
});

export const {
  useFetchLicenseByLicenseKeyQuery,
  useFetchLicenseListQuery,
  useLazyFetchLicenseListQuery,
  useFetchLicenseSelfServeRenewableListQuery,
  useAssignLicenseMutation,
  useAddLicenseMutation,
  useFetchLicenseAuditHistoryByLicenseIdQuery,
  useDeleteLicenseMutation,
  useEditLicenseMutation,
  useUnassignLicenseMutation,
  useRenewLicenseMutation,
  useSelfServeRenewLicenseMutation,
  useAlignLicenseExpiryInfoMutation,
  useAlignLicenseExpiryMutation,
} = licenseApi;
